import { zodResolver } from '@hookform/resolvers/zod';
import { QuoteLine, TimesheetDepartment, User } from '@kalos/kalos-rpc';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  MultipleSelect,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@kalos/ui';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { default as IconButton } from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAuth } from '../../../context/AuthContext';
import {
  QuoteLineClientService,
  TimesheetDepartmentClientService,
  UserClientService,
} from '../../../tools/helpers';
import { PageWrapper } from '../../PageWrapper/main';
import { SectionBar } from '../SectionBar';
import { FlatRateCreateForm } from './components/FlatRateCreateForm';

export interface Props {
  onClose?: () => void;
  initialDepartment?: number;
  readOnly?: boolean;
  onSelect?: (flatRate: QuoteLine) => void;
}

// Define the Zod schemas
const searchFormSchema = z.object({
  description: z.string().optional(),
  department: z.array(z.union([z.number(), z.literal('all')])).optional(),
});

const editFormSchema = z.object({
  id: z.number().optional(),
  description: z
    .string()
    .min(1, "Description can't be empty")
    .max(1000, 'Description is too long (max 1000 characters)'),
  department: z.array(z.number()),
  cost: z.number().min(0.0, 'Cost must be greater than or equal to 0'),
  excludeInvoiceDiscount: z.boolean().default(false),
});

// Define the types
type SearchFormData = z.infer<typeof searchFormSchema>;
type EditFormData = z.infer<typeof editFormSchema>;

export const FlatRateSheet: FC<Props> = function FlatRateSheet({
  onClose,
  initialDepartment,
  readOnly = false,
  onSelect,
}) {
  const auth = useAuth();
  const loggedUserId = auth.user.id;

  // State
  const [loaded, setLoaded] = useState(false);
  const [flatRates, setFlatRates] = useState<QuoteLine[]>([]);
  const [filteredFlatRates, setFilteredFlatRates] = useState<QuoteLine[]>([]);
  const [departmentList, setDepartmentList] = useState<TimesheetDepartment[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<number>(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [canEditFlatRates, setCanEditFlatRates] = useState(false);
  const [canEditDepartment, setCanEditDepartment] = useState(false);

  // Initialize the search form
  const searchForm = useForm<SearchFormData>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: {
      description: '',
      department: initialDepartment ? [initialDepartment] : ['all'],
    },
  });

  // Initialize the edit form
  const editForm = useForm<EditFormData>({
    resolver: zodResolver(editFormSchema),
    mode: 'onChange',
    defaultValues: {
      description: '',
      department: [0],
      cost: 0,
      excludeInvoiceDiscount: false,
    },
  });

  // Load user permissions
  const getUserData = useCallback(async () => {
    if (loggedUserId) {
      const userObj = User.create();
      userObj.id = loggedUserId;
      try {
        const user = await UserClientService.Get(userObj);
        const canEditFlatRate = user.permissionGroups.find((p) => p.name === 'EditFlatRates');
        const canEditDepartments = user.permissionGroups.find(
          (p) => p.name === 'EditFlatRateDepartment',
        );
        setCanEditFlatRates(canEditFlatRate !== undefined);
        setCanEditDepartment(canEditDepartments !== undefined);
      } catch (err) {
        console.error('Failed to get user data', err);
      }
    }
  }, [loggedUserId]);

  // Load departments
  const getDepartments = useCallback(async () => {
    const dep = TimesheetDepartment.create();
    dep.isActive = 1;
    try {
      const departmentList = await TimesheetDepartmentClientService.BatchGet(dep);
      setDepartmentList(departmentList?.results || []);
    } catch (err) {
      console.error('Unable to Retrieve Department List', err);
    }
  }, []);

  // Filter flat rates based on search criteria
  const filterFlatRates = useCallback(
    (rates: QuoteLine[], searchParams: SearchFormData) => {
      const filteredDepartments = searchParams.department;

      return rates.filter((rate) => {
        // Filter by description if search term provided
        if (searchParams.description) {
          const searchTerm = searchParams.description.toLowerCase();
          const description = rate.description.toLowerCase();
          if (!description.includes(searchTerm)) {
            return false;
          }
        }

        // Filter by department
        const rateDepartments = rate.departments.split(',').map(Number);

        // If "ALL" is selected, don't filter by department at all
        if (filteredDepartments && filteredDepartments.some((dep) => dep === 'all')) {
          return true; // Skip department filtering
        }

        // If no departments are selected (empty array), show all rates
        if (!filteredDepartments || filteredDepartments.length === 0) {
          return true;
        }

        // Convert filtered departments to numbers only (filter out 'all' if present)
        const numericDepartments = filteredDepartments?.filter(
          (dep): dep is number => typeof dep === 'number',
        );

        if (numericDepartments.includes(0)) {
          // If 'None' is selected, only show rates with departments exactly [0]
          if (rateDepartments.length === 1 && rateDepartments[0] === 0) {
            return true;
          }
          return false;
        } else {
          // Otherwise, show rates that match any selected department
          const departmentMatch = numericDepartments.some((depId) =>
            rateDepartments.includes(depId),
          );
          if (!departmentMatch) {
            return false;
          }
        }

        return true;
      });
    },
    [], // Removed initialDepartment from dependencies since we're not using it anymore
  );

  // Load flat rates
  const getFlatRates = useCallback(async () => {
    try {
      const quoteReq = QuoteLine.create();
      quoteReq.isActive = 1;
      quoteReq.isFlatrate = '1';
      quoteReq.pageNumber = 0;
      quoteReq.withoutLimit = true;

      const results = (await QuoteLineClientService.BatchGet(quoteReq))?.results || [];

      // Filter out flat rate items that have been assigned to specific jobs
      const catalogOnlyResults = results.filter(
        (item) => !item.jobNumber || item.jobNumber.trim() === '',
      );

      // Sort by description
      const sortedResults = [...catalogOnlyResults].sort((a, b) => {
        if (a.description < b.description) return -1;
        if (a.description > b.description) return 1;
        return 0;
      });

      setFlatRates(sortedResults);

      // Apply current filters
      const searchValues = searchForm.getValues();
      const filtered = filterFlatRates(sortedResults, searchValues);
      setFilteredFlatRates(filtered);

      setLoaded(true);
    } catch (e) {
      console.error('Could not fetch results for flat rate', e);
      setLoaded(true);
    }
  }, [filterFlatRates, searchForm]);

  // Handle search form submission
  const handleSearch = useCallback(
    (data: SearchFormData) => {
      const filtered = filterFlatRates(flatRates, data);
      setFilteredFlatRates(filtered);
    },
    [filterFlatRates, flatRates],
  );

  // Handle edit button click
  const handleEditClick = useCallback(
    (id: number) => {
      const flatRate = flatRates.find((fr) => fr.id === id);
      if (flatRate) {
        const departments = flatRate.departments.split(',').map(Number);
        editForm.reset({
          id: flatRate.id,
          description: flatRate.description,
          department: departments,
          cost: Number(flatRate.adjustment),
          excludeInvoiceDiscount: flatRate.excludeInvoiceDiscount === 1,
        });
        setIsEditing(true);
        setSelectedRowId(id);
      }
    },
    [flatRates, editForm],
  );

  // Handle save (update) of flat rate
  const handleSaveEdit = useCallback(
    async (data: EditFormData) => {
      setSaving(true);
      try {
        const flatRate = QuoteLine.create();
        flatRate.id = selectedRowId;
        flatRate.description = data.description;
        flatRate.departments = data.department.toString();
        flatRate.adjustment = data.cost.toString();
        flatRate.excludeInvoiceDiscount = data.excludeInvoiceDiscount ? 1 : 0;
        flatRate.fieldMask = ['Description', 'Departments', 'Adjustment', 'ExcludeInvoiceDiscount'];

        await QuoteLineClientService.Update(flatRate);

        setIsEditing(false);
        setSelectedRowId(0);
        await getFlatRates();
      } catch (err) {
        console.error('Failed to Save Flat Rate', err);
      } finally {
        setSaving(false);
      }
    },
    [selectedRowId, getFlatRates],
  );

  // Handle delete
  const handleDelete = useCallback(
    async (id: number) => {
      try {
        const quoteReq = QuoteLine.create();
        quoteReq.id = id;
        await QuoteLineClientService.Delete(quoteReq);
        setIsDeleting(false);
        setSelectedRowId(0);
        await getFlatRates();
      } catch (err) {
        console.error('Failed to Delete Flat Rate', err);
      }
    },
    [getFlatRates],
  );

  // Handle creation of new flat rate
  const handleCreateFormSave = useCallback(
    async ({
      data,
    }: {
      data: {
        description: string;
        departments: number[];
        adjustment: number;
        excludeInvoiceDiscount: boolean;
      };
    }) => {
      if (isSaving) return; // Prevent duplicate submissions

      setSaving(true);
      try {
        const flatRate = QuoteLine.create();
        flatRate.description = data.description;
        flatRate.departments = data.departments.toString();
        flatRate.adjustment = data.adjustment.toString();
        flatRate.excludeInvoiceDiscount = data.excludeInvoiceDiscount ? 1 : 0;
        flatRate.isActive = 1;
        flatRate.isFlatrate = '1';

        await QuoteLineClientService.Create(flatRate);
        setIsCreating(false);
        await getFlatRates();
      } catch (err) {
        console.error('Failed to Create Flat Rate', err);
        throw err; // Re-throw to be caught by the form's error handling
      } finally {
        setSaving(false);
      }
    },
    [getFlatRates, isSaving],
  );

  // Initialize component
  useEffect(() => {
    if (!loaded) {
      getUserData();
      getDepartments();
      getFlatRates();
    }
  }, [loaded, getUserData, getDepartments, getFlatRates]);

  // Watch for search form changes
  useEffect(() => {
    const subscription = searchForm.watch((value) => {
      handleSearch(value as SearchFormData);
    });
    return () => subscription.unsubscribe();
  }, [searchForm, handleSearch]);

  const departmentOptions = [
    { value: 'all', label: 'ALL' },
    { value: '0', label: 'None' },
    ...departmentList.map((dl) => ({
      value: dl.id.toString(),
      label: `${dl.value} - ${dl.description}`,
    })),
  ];

  const renderDepartments = (departments: string) => {
    if (departments === '0') {
      return (
        <Badge variant="secondary" className="text-xs">
          All Departments
        </Badge>
      );
    }
    return (
      <div className="flex flex-wrap gap-1">
        {departments.split(',').map((id) => {
          const dept = departmentList.find((d) => String(d.id) === id.trim());
          return dept ? (
            <Badge key={id} variant="outline" className="text-xs">
              {dept.description}
            </Badge>
          ) : null;
        })}
      </div>
    );
  };

  const showActions = !readOnly && canEditFlatRates;
  const colSpan = showActions ? 5 : 4;

  return (
    <PageWrapper>
      <SectionBar
        title="Flat Rates"
        actions={
          onClose
            ? [
                {
                  label: isCreating ? 'Cancel Add' : 'Add New Flat Rate',
                  onClick: () => setIsCreating(!isCreating),
                  style: { display: !readOnly && canEditFlatRates ? '' : 'none' },
                },
                {
                  label: 'Close',
                  onClick: onClose,
                },
              ]
            : [
                {
                  label: isCreating ? 'Cancel Add' : 'Add New Flat Rate',
                  onClick: () => setIsCreating(!isCreating),
                  style: { display: !readOnly && canEditFlatRates ? '' : 'none' },
                },
              ]
        }
        uncollapsable
      />

      {/* Search Form */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Search Flat Rates</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...searchForm}>
            <form className="flex flex-col gap-4 md:flex-row">
              <FormField
                control={searchForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Search by description..."
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={searchForm.control}
                name="department"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormLabel>Department(s)</FormLabel>
                    <FormControl>
                      <MultipleSelect
                        placeholder="Select departments..."
                        options={departmentOptions}
                        selected={field.value?.map((v) => v.toString()) || []}
                        onChange={(values) => {
                          // Handle both string "all" and numeric values
                          const processedValues = values.map((v) => {
                            if (v === 'all') return 'all' as any;
                            return parseInt(v);
                          });
                          field.onChange(processedValues);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Create Form */}
      {isCreating && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Add New Flat Rate</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <FlatRateCreateForm
              onSave={handleCreateFormSave}
              isLoading={isSaving}
              canEditDepartment={canEditDepartment}
              initialDepartment={initialDepartment}
            />
          </CardContent>
        </Card>
      )}

      {/* Flat Rates Table */}
      <div className="overflow-x-auto rounded-md border [&_thead]:bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Description</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Cost</TableHead>
              <TableHead>Excl. Discount</TableHead>
              {showActions && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loaded &&
              Array.from({ length: 7 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: colSpan }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {loaded && filteredFlatRates.length === 0 && (
              <TableRow>
                <TableCell colSpan={colSpan} className="h-24 text-center text-sm text-gray-500">
                  No flat rates found.
                </TableCell>
              </TableRow>
            )}

            {loaded &&
              filteredFlatRates.flatMap((value) => {
                const rows = [];

                if (isDeleting && selectedRowId === value.id) {
                  rows.push(
                    <TableRow key={value.id} className="bg-red-50">
                      <TableCell colSpan={colSpan}>
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-sm font-semibold">
                            Are you sure you want to delete this record?
                          </span>
                          <div className="flex gap-1">
                            <Tooltip title="Confirm">
                              <IconButton size="small" onClick={() => handleDelete(value.id)}>
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setIsDeleting(false);
                                  setSelectedRowId(0);
                                }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>,
                  );
                  return rows;
                }

                const isClickable = !!onSelect && !isEditing && !isDeleting;
                const isActiveEdit = isEditing && selectedRowId === value.id;
                rows.push(
                  <TableRow
                    key={value.id}
                    onClick={isClickable ? () => onSelect(value) : undefined}
                    className={isActiveEdit ? 'bg-muted/20' : isClickable ? 'cursor-pointer' : ''}
                  >
                    <TableCell>{value.description}</TableCell>
                    <TableCell>{renderDepartments(value.departments)}</TableCell>
                    <TableCell className="font-medium">
                      ${parseFloat(value.adjustment).toFixed(2)}
                    </TableCell>
                    <TableCell>{value.excludeInvoiceDiscount === 1 ? 'Yes' : 'No'}</TableCell>
                    {showActions && (
                      <TableCell>
                        {!isActiveEdit && (
                          <div className="flex items-center">
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditClick(value.id)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setIsDeleting(true);
                                  setSelectedRowId(value.id);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>,
                );

                if (isEditing && selectedRowId === value.id) {
                  rows.push(
                    <TableRow key={`edit-${value.id}`} className="hover:bg-transparent">
                      <TableCell colSpan={colSpan} className="p-0">
                        <div className="animate-in fade-in-0 slide-in-from-top-1 bg-muted/30 border-t p-5 duration-200">
                          <Form {...editForm}>
                            <form
                              onSubmit={editForm.handleSubmit(handleSaveEdit)}
                              className="space-y-4"
                            >
                              <FormField
                                control={editForm.control}
                                name="description"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Description</FormLabel>
                                    <FormControl>
                                      <Textarea
                                        {...field}
                                        placeholder="Enter description"
                                        rows={5}
                                      />
                                    </FormControl>
                                    <FormMessage className="text-red-500" />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={editForm.control}
                                name="department"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Department(s)</FormLabel>
                                    <FormControl>
                                      <MultipleSelect
                                        placeholder="Select departments..."
                                        options={departmentOptions}
                                        selected={field.value.map((v) => v.toString())}
                                        onChange={(values) => field.onChange(values.map(Number))}
                                        disabled={!canEditDepartment}
                                      />
                                    </FormControl>
                                    <FormMessage className="text-red-500" />
                                    {!canEditDepartment && (
                                      <div className="text-muted-foreground mt-1 text-sm">
                                        Department editing requires permission
                                      </div>
                                    )}
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={editForm.control}
                                name="cost"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Cost</FormLabel>
                                    <FormControl>
                                      <Input
                                        type="number"
                                        placeholder="Enter cost"
                                        {...field}
                                        value={field.value || 0}
                                        onChange={(e) => field.onChange(parseFloat(e.target.value))}
                                      />
                                    </FormControl>
                                    <FormMessage className="text-red-500" />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={editForm.control}
                                name="excludeInvoiceDiscount"
                                render={({ field }) => (
                                  <FormItem className="flex flex-row items-center gap-2">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                    </FormControl>
                                    <FormLabel className="mb-0">
                                      Exclude from Invoice Discount
                                    </FormLabel>
                                  </FormItem>
                                )}
                              />

                              <div className="flex justify-end space-x-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    setIsEditing(false);
                                    setSelectedRowId(0);
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button type="submit" disabled={isSaving} isLoading={isSaving}>
                                  Save
                                </Button>
                              </div>
                            </form>
                          </Form>
                        </div>
                      </TableCell>
                    </TableRow>,
                  );
                }

                return rows;
              })}
          </TableBody>
        </Table>
      </div>
    </PageWrapper>
  );
};
